import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    map,
    shareReplay,
    startWith,
    take,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import { DetailConstructionDiaryEntryComponent, DetailConstructionDiaryEntryComponentDialogData } from '../../../../dave-commission-module/components/detail-commission/detail-construction-diary-entry/detail-construction-diary-entry.component';
import { CommissionEntity } from '../../../../dave-data-module/entities/commission.entity';
import { PENDING_CHANGES_DEFAULT_MESSAGE } from '../../../../dave-data-module/guards/pending-changes.guard';
import { State } from '../../../../dave-data-module/State';
import {
    getCommissions,
    getCommissionsActive,
    getCommissionsFetched,
} from '../../../../dave-data-module/State/selectors/commission.selector';
import {
    getCommissionTypes,
    getCommissionTypesFetched,
} from '../../../../dave-data-module/State/selectors/commissionType.selectors';
import { getSetting, getUser } from '../../../../dave-data-module/State/selectors/users.selectors';
import { DetailTasksComponent, DetailTasksComponentDialogData } from '../../../../dave-event-card/components/detail-tasks/detail-tasks.component';
import { SelectFolderDialogComponent, SelectFolderDialogComponentDialogData } from '../../../../dave-file-explorer/components/select-folder-dialog/select-folder-dialog.component';
import { ListCardDialogComponent, ListCardDialogComponentDialogData } from '../../../../dave-list-card/components/list-card-dialog/list-card-dialog.component';
import { SearchQueriesDebounceTime } from '../../../../helper/helper';
import { DMSPageMeta, InvoiceEditorMeta, MaterialPageMeta, UserAdministrationMeta } from '../../../../helper/page-metadata';
import { ClockInDataService } from '../../../../dave-data-module/services/clockin-data.service';
import { CustomLabelService } from '../../../../services/custom-label/custom-label.service';
import { DashboardHelperService } from '../../../../services/dashboard-helper.service';
import { DayTimeTrackerComponent } from '../../../../time-tracker/components/day-time-tracker/day-time-tracker.component';
import { TimeTrackerMultipleComponent } from '../../../../time-tracker/components/time-tracker-multiple/time-tracker-multiple.component';
import { WarehouseMovementComponent } from '../../../../warehouse/warehouse-movement/warehouse-movement.component';
import { DaveFileUploadDialogComponentDialogData } from '../../../templates/new-document-view/component/dave-file-upload-dialog/dave-file-upload-dialog.component';
import { TimeTrackerKagetecComponent, TimeTrackerKagetecDialogData } from '../time-tracker-kagetec/time-tracker-kagetec.component';
import { ExportBusinessVolumeDialogComponentDialogData, BusinessVolumesDialogComponent } from '../../../../detail-list-template-dialog/business-volumes-dialog/business-volumes-dialog.component';
import { getCustomers } from '../../../../dave-data-module/State/selectors/customers.selectors';
import {
    FILTER_TYPE_SEARCH_MULTI_SELECT,
    FilterOption, IFilterTypeSearchMultiSelectValue,
} from '../../../../dave-utils-module/app-filter-module/app-filter/app-filter.component';
import { FilterTypes } from '../../../../services/default-filter.service';
import { CustomerEntity } from '../../../../dave-data-module/entities/customer.entity';
import { CommissionResolver } from '../../../../dave-data-module/guards/commission.resolver';
import { CommissionTypeResolver } from '../../../../dave-data-module/guards/commissionType.resolver';

export interface IDiaryCommissionFilterType {
    [FilterTypes.Customers]: IFilterTypeSearchMultiSelectValue[];
}

@Component({
    selector: 'app-service-buttons',
    templateUrl: './service-buttons.component.html',
    styleUrls: ['./service-buttons.component.scss'],
})
export class ServiceButtonsComponent implements OnInit, OnDestroy {
    @Input() FileUploadConfig: DaveFileUploadDialogComponentDialogData = {};

    private resolverSubscription: Subscription;
    public Buttons$: Observable<{ name: string; theme: string; icon: IconProp; icon2?: IconProp; tooltip: string; }[]> = combineLatest([
        this.clockInDataService.getUserSettingsForClockIns$(),
        this.dashboardHelperService.GetDashboardItems$(),
        this.store.select(getSetting).pipe(
            map((s) => s.ShowWarehouseMovementOnDashboard),
            distinctUntilChanged(),
        ),
        this.store.select(getSetting).pipe(
            map((s) => s.ShowOpListOnDashboard),
            distinctUntilChanged(),
        ),
    ]).pipe(
        map(([settings, dashboardItems, showWarehouseMovementOnDashboard, showOpListOnDashboard]) => {
            const cards = dashboardItems
                .filter((card) => !card.Disabled && card.Buttons?.length)
                .map((c) => c.Buttons.map((b) => ({ ...b, theme: c.Theme, tooltip: '' })))
                .flat();


            const add: { name: string; icon: IconProp; icon2?: IconProp; theme: string; tooltip: string }[] = [];

            if (settings.ShowTimeTrackingClockWatch) {
                add.push({
                    name: 'timer',
                    icon: 'stopwatch',
                    theme: UserAdministrationMeta.Theme,
                    tooltip: '',
                });
            }
            if (settings.ShowTimeTrackingPopup) {
                add.push({
                    name: 'timer-popup',
                    icon: 'stopwatch',
                    theme: UserAdministrationMeta.Theme,
                    tooltip: '',
                });
            }
            if (settings.ShowTimeTrackingMultiplePopup) {
                add.push({
                    name: 'timer-multiple-popup',
                    icon: 'stopwatch',
                    theme: UserAdministrationMeta.Theme,
                    tooltip: '',
                });
            }
            if (settings.ShowDayTimeTrackingPopup) {
                add.push({
                    name: 'day-timer-popup',
                    icon: 'stopwatch',
                    theme: UserAdministrationMeta.Theme,
                    tooltip: '',
                });
            }
            // add.push({
            //     name: 'photo-button',
            //     icon: 'camera',
            //     theme: DMSPageMeta.Theme,
            //     tooltip: '',
            // });
            if (showWarehouseMovementOnDashboard) {
                add.push({
                    name: 'warehouse-movement',
                    icon: 'warehouse',
                    icon2: 'up-right-and-down-left-from-center',
                    theme: MaterialPageMeta.Theme,
                    tooltip: 'Wareneingang / Warenausgang anlegen',
                });
            }
            if (showOpListOnDashboard) {
                add.push({
                    name: 'op-list',
                    icon: 'download',
                    icon2: 'list',
                    theme: InvoiceEditorMeta.Theme,
                    tooltip: 'OP-Liste',
                });
            }
            // if (salaryType === SalaryTypes.PerHour) {
            cards.unshift(...add);
            // } else {
            //     cards.push(...add);
            // }
            return cards;
        }),
        shareReplay({bufferSize: 1, refCount: true})
    );
    constructor(
        private dashboardHelperService: DashboardHelperService,
        private clockInDataService: ClockInDataService,
        private store: Store<State>,
        private dialog: MatDialog,
        protected cls: CustomLabelService,
        private commissionResolver: CommissionResolver,
        private commissionTypeResolver: CommissionTypeResolver) {}

    ngOnDestroy(): void {
        this.resolverSubscription?.unsubscribe();
    }

    ngOnInit(): void {
        this.resolverSubscription = this.Buttons$.subscribe(btns => {
            if (btns.some(btn => btn.name === 'diary-button')) {
                firstValueFrom(this.store.select(getCommissionTypesFetched)).then((fetched) => {
                    if (!fetched) {
                        this.commissionTypeResolver.resolve();
                    }
                });
                firstValueFrom(this.store.select(getCommissionsFetched)).then((fetched) => {
                    if (!fetched) {
                        this.commissionResolver.resolve();
                    }
                });
            }
        });
    }

    ShowButton(actionName: string): Observable<boolean> {
        if (actionName === 'diary-button') {
            return combineLatest([this.store.select(getCommissionTypes), this.store.select(getCommissions)]).pipe(
                map(([ct, commissions]) => {
                    const typesWithBtgb = ct?.filter((c) => c.AdditionalData?.ConstructionDiary).map((c) => c.Id);
                    return commissions.some((c) => c.CommissionTypeId && typesWithBtgb.includes(c.CommissionTypeId) && !c.Deleted);
                }),
            );
        }
        if (actionName === 'photo-button') {
            return of(true);
        }
        if (actionName === 'warehouse-movement') {
            return of(true);
        }
        if (actionName === 'op-list') {
            return of(true);
        }
        if (actionName === 'add-task-button') {
            return of(true);
        }
        if (actionName === 'timer-popup') {
            return of(true);
        }
        if (actionName === 'timer-multiple-popup') {
            return of(true);
        }
        if (actionName === 'day-timer-popup') {
            return of(true);
        }
    }

    ButtonClickHandler(actionName: string) {
        if (actionName === 'diary-button') {
            this.store
                .select(getCommissionsActive)
                .pipe(take(1), withLatestFrom(
                    this.store.select(getUser),
                    this.store.select(getCommissionTypes),
                    this.cls.getMultiple$('Commission'),
                    this.store.select(getCustomers),
                    this.cls.getSingle$('CustomerSupplier'),
                ))
                .subscribe(([commissions, user, commissionTypes, commissionLabel, customers, customerLabel]) => {
                    const data = commissions
                        .filter((c) => /* c.UserIds?.includes(user.Id) && Alexander Bogdanow 14:57 */ commissionTypes.some((ct) => ct.Id === c.CommissionTypeId && ct.AdditionalData?.ConstructionDiary))
                        .sort((a, b) => {
                            if (a.UpdatedAt.getTime() < b.UpdatedAt.getTime()) {
                                return 1;
                            }
                            if (b.UpdatedAt.getTime() < a.UpdatedAt.getTime()) {
                                return -1;
                            }
                            return 0;
                        });
                    let filteredData$ = new BehaviorSubject(data.slice());

                    const filterSettings = this.buildDiaryCommissionFilterSettings(customers, customerLabel);

                    const dialogRef = this.dialog.open<ListCardDialogComponent<CommissionEntity, IDiaryCommissionFilterType>,
                        ListCardDialogComponentDialogData<CommissionEntity, IDiaryCommissionFilterType>>(
                            ListCardDialogComponent,
                        {
                        ...ListCardDialogComponent.DefaultConfig,
                        data: {
                            Headline: commissionLabel,
                            EmptyText: 'Keine aktiven ' + commissionLabel + ' vorhanden',
                            Data$: filteredData$,
                            DisplayedCollums: ['optionLabelReverse'],
                            Clickable: true,
                            HasSuffixButton: false,
                            Search: true,
                            Filter: {
                                FilterSettings: filterSettings,
                                InitialFilterValues: {
                                    [FilterTypes.Customers]: [],
                                },
                            }
                        },
                    });
                    dialogRef.afterOpened().subscribe((d) => {
                        const searchFilter$ = dialogRef.componentInstance.SearchForm.valueChanges.pipe(
                                takeUntil(dialogRef.afterClosed()),
                                debounceTime(SearchQueriesDebounceTime),
                                map((s) => s?.trim().toLowerCase() || ''),
                                startWith(''),
                            );

                        combineLatest([
                            searchFilter$,
                            dialogRef.componentInstance.filterValues$,
                        ]).pipe(
                            takeUntil(dialogRef.afterClosed()),
                        ).subscribe(([searchString, filterValues]) => {
                            let result = this.applyDiaryCommissionFilter(data, filterValues);
                            result = this.applySearchToCommissionList(result, searchString);

                            filteredData$.next(result);
                        });

                        dialogRef.componentInstance.EntryClicked$.pipe(takeUntil(dialogRef.afterClosed())).subscribe((event) => {
                            dialogRef.close();
                            const commissionId = event.entry.Id;
                            const date = new Date();
                            this.dialog
                                .open<DetailConstructionDiaryEntryComponent, DetailConstructionDiaryEntryComponentDialogData>(DetailConstructionDiaryEntryComponent, {
                                    ...DetailConstructionDiaryEntryComponent.DefaultConfig,
                                    data: {
                                        CommissionId: commissionId,
                                        DiaryDate: date,
                                    },
                                })
                                .afterClosed()
                                .subscribe((res) => {
                                    if (res === 'open-time-tracking-kagetec') {
                                        this.dialog.open<TimeTrackerKagetecComponent, TimeTrackerKagetecDialogData>(TimeTrackerKagetecComponent, {
                                            ...TimeTrackerKagetecComponent.DefaultConfig,
                                            data: {
                                                DefaultValues: {
                                                    CommissionId: commissionId,
                                                    Date: date,
                                                },
                                            },
                                        });
                                    }
                                });
                        });
                    });
                });
        }

        if (actionName === 'add-task-button') {
            this.OpenDetailTaskDialog();
        }
        if (actionName === 'timer-popup') {
            this.OpenTimeDialog();
        }
        if (actionName === 'timer-multiple-popup') {
            this.OpenTimeMultiDialog();
        }
        if (actionName === 'day-timer-popup') {
            this.OpenDayTimerDialog();
        }
        if (actionName === 'photo-button') {
            const dialogRef = this.dialog.open<SelectFolderDialogComponent, SelectFolderDialogComponentDialogData>(SelectFolderDialogComponent, {
                ...SelectFolderDialogComponent.DefaultConfig,
                panelClass: [...(SelectFolderDialogComponent.DefaultConfig.panelClass || []), 'no-header'],
                data: {
                    ButtonText: 'HOCHLADEN',
                    matTreeNodePaddingIndent: 10,
                    uploadMode: true,
                },
            });
            // firstValueFrom(this.folderTreeService.Tree).then((folder) => {
            //     dialogRef.componentInstance.TreeDataSource = new MatTreeFlatDataSource(folderTreeControl, folderTreeFlattener, folder);
            // });
            // Tree Data Source
            // firstValueFrom(this.store.select(getFolderFetched)).then(f => {
            //          if (!f) {
            //            this.folderResolver.resolve();
            //          }
            //        });
            // this.store.select(getFolderFetched).pipe(
            //   tap(f => {
            //     if (!f) {
            //       this.folderResolver.resolve();
            //     }
            //   }),
            // filter(f => f),
            // switchMap(() => this.store.select(getFoldersWithoutHidden)),
            // take(1))
            //       .subscribe((folders) => {
            //           const selectedFolders = folders;
            //           // !f.PartnerId für den "GeteilteAufträge" Ordner
            //           const filteredFolders = selectedFolders?.length ? folders.filter((f) => !f.PartnerId || f.PartnerId === selectedFolders[0].PartnerId) : folders;
            //           const root = filteredFolders
            //               .slice()
            //               .filter((f) => !isNotNullOrUndefined(f.ParentId))
            //               .sort(sortByName);
            //           const data = root.map((f) => mapfoldertofood(f, filteredFolders.slice()).foodNodes);
            //
            //           const rootNode: FoodNode = {
            //               name: 'Hauptordner',
            //               children: data,
            //               Id: null,
            //               isFolder: true,
            //               disabled: false,
            //           };
            //           dialogRef.componentInstance.TreeDataSource = new MatTreeFlatDataSource(folderTreeControl, folderTreeFlattener, [rootNode]);
            //       });
            //const selectDataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
            //this.dialog
            //    .open<DaveFileUploadDialogComponent, DaveFileUploadDialogComponentDialogData>(DaveFileUploadDialogComponent, {
            //      ...DaveFileUploadDialogComponent.DefaultConfig,
            //    data: { ...this.FileUploadConfig, FolderId: 0 },
            // })
            //   .afterClosed()
            //  .subscribe(() => {
            //      this.store
            //          .select(getFiles)
            //           .pipe(skip(1), take(1))
            //           .subscribe((files) => {
            //               const latestFile = [files.reduce((id, file) => (file.Id > id ? file.Id : id), -1)];
            //               this.moveFileFolderService.MoveFileFolder(latestFile, [], parentFolderId);
            //           });
            //   });
        }
        if (actionName === 'warehouse-movement') {
            this.onAddWarehouseMovement();
        }
        if (actionName === 'op-list') {
            this.OpenOpList();
        }
    }
    onAddWarehouseMovement() {
        this.dialog.open(WarehouseMovementComponent, {
            ...WarehouseMovementComponent.DefaultConfig,
        });
    }
    OpenOpList() {
        this.dialog.open<BusinessVolumesDialogComponent, ExportBusinessVolumeDialogComponentDialogData>(BusinessVolumesDialogComponent, {
            data: {
                Headline: "Buchhaltungsdaten exportieren",
            },
            ...BusinessVolumesDialogComponent.DefaultConfig,
        });
    }
    public OpenTimeMultiDialog() {
        firstValueFrom(this.store.select(getSetting).pipe(map((s) => s.DarkMode))).then((isDarkTheme) => {
            this.dialog.open(TimeTrackerMultipleComponent, {
                ...TimeTrackerMultipleComponent.DefaultConfig,
                panelClass: [...TimeTrackerMultipleComponent.DefaultConfig.panelClass, UserAdministrationMeta.Theme, ...(isDarkTheme ? ['dark-theme'] : [])],
            });
        });
    }
    public OpenTimeDialog() {
        this.dialog.open(TimeTrackerKagetecComponent, TimeTrackerKagetecComponent.DefaultConfig);
    }
    public OpenDayTimerDialog() {
        const dialogRef = this.dialog.open(DayTimeTrackerComponent, {
            ...DayTimeTrackerComponent.DefaultConfig,
            disableClose: true,
        });
        dialogRef
            .backdropClick()
            .pipe(takeUntil(dialogRef.beforeClosed()))
            .subscribe((event) => {
                if (dialogRef.componentInstance.canDeactivate() || confirm(PENDING_CHANGES_DEFAULT_MESSAGE)) {
                    dialogRef.close();
                }
            });
    }

    OpenDetailTaskDialog(taskId?: number) {
        this.dialog.open<DetailTasksComponent, DetailTasksComponentDialogData>(DetailTasksComponent, {
            ...DetailTasksComponent.DefaultConfig,
            data: {
                EventId: taskId || null,
            },
        });
    }

    private buildDiaryCommissionFilterSettings(customers: CustomerEntity[], customerLabel: string): FilterOption[] {
        return [
            {
                Name: FilterTypes.Customers,
                Type: FILTER_TYPE_SEARCH_MULTI_SELECT,
                Label: customerLabel,
                Values: customers.map((customer) => ({
                    label: customer.DisplayName,
                    id: customer.Id,
                })),
            },
        ];
    }

    private applySearchToCommissionList(commissions: CommissionEntity[], search: string): CommissionEntity[] {
        if (!search) return commissions;

        return commissions.filter((c) =>
            [c.DisplayName, c.CustomerName]
                .filter(Boolean)
                .map((v) => v.toLowerCase())
                .some((v) => v.includes(search))
        );
    }

    private applyDiaryCommissionFilter(commissions: CommissionEntity[], filters: IDiaryCommissionFilterType): CommissionEntity[] {
        if (!filters) return commissions;

        let filtered = commissions;

        if (filters[FilterTypes.Customers]?.length) {
            filtered = filtered.filter((commission) =>
                filters[FilterTypes.Customers].some(
                    (f) => f.id === commission.CustomerId
                )
            );
        }

        return filtered;
    }
}
